import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { playerAPI } from '../../services/api';

const Wrap = styled.div`
  margin-top: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const Title = styled.div`
  font-size: 0.92rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
`;

const ToggleTrack = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 52px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 999px;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.disabled ? 0.55 : 1)};
  transition: background 0.2s ease, box-shadow 0.2s ease;

  background: ${(p) =>
    p.$on
      ? 'linear-gradient(180deg, #34d399 0%, #059669 55%, #047857 100%)'
      : 'linear-gradient(180deg, #e5e7eb 0%, #cbd5e1 55%, #94a3b8 100%)'};

  box-shadow: ${(p) =>
    p.$on
      ? 'inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 4px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.08)'
      : 'inset 0 2px 4px rgba(0,0,0,0.12), inset 0 -1px 0 rgba(255,255,255,0.5), 0 1px 2px rgba(0,0,0,0.06)'};

  &:focus-visible {
    outline: 2px solid #043663;
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const ToggleKnob = styled.span`
  position: absolute;
  top: 3px;
  left: ${(p) => (p.$on ? '27px' : '3px')};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  transition: left 0.2s ease, box-shadow 0.2s ease;

  background: linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%);
  box-shadow:
    0 2px 4px rgba(15, 23, 42, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -1px 2px rgba(148, 163, 184, 0.35);
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * @param {object} props
 * @param {boolean} props.profilePublic
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 */
export default function ProfilePublicToggle({ profilePublic, disabled, className }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const on = profilePublic === true;

  const handleToggle = async () => {
    if (disabled || saving) return;
    const next = !on;
    setSaving(true);
    try {
      const res = await playerAPI.updateProfile({ profilePublic: next });
      if (res?.success === false) {
        toast.error(res?.message || t('phase2.playerProfile.toasts.updateFailed'));
        return;
      }
      queryClient.invalidateQueries(['playerProfile']);
      queryClient.invalidateQueries(['openPlayers']);
      toast.success(
        next
          ? t('phase2.playerProfile.toasts.profilePublicOn')
          : t('phase2.playerProfile.toasts.profilePublicOff')
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || t('phase2.playerProfile.toasts.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrap className={className}>
      <Row>
        <Title>{t('phase2.playerProfile.labels.profilePublicToggle')}</Title>
        <ToggleTrack
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={t('phase2.playerProfile.labels.profilePublicToggle')}
          $on={on}
          disabled={disabled || saving}
          onClick={handleToggle}
        >
          <VisuallyHidden>
            {on ? t('phase2.playerProfile.toggleOn') : t('phase2.playerProfile.toggleOff')}
          </VisuallyHidden>
          <ToggleKnob $on={on} aria-hidden />
        </ToggleTrack>
      </Row>
    </Wrap>
  );
}
